import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Table, Badge, Spinner, Form } from 'react-bootstrap';
import { UPLOAD_BASE_URL } from '../../config';

interface OrderDetailsModalProps {
  show: boolean;
  onHide: () => void;
  selectedOrder: any;
  updatingOrderStatusId: string | null;
  jobCardsList: any[];
  onOpenTicket: (job: any) => void;
  onOpenCreateJobCard: (order: any) => void;
  onUpdateStatus: (orderId: string, newStatus: string) => Promise<void>;
  onUpdateOrder?: (input: any) => Promise<void>;
  isInitialEditMode?: boolean;
}

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'pending':
      return <Badge bg="warning" text="dark" className="px-2 py-1 fw-semibold text-capitalize rounded-pill">Pending</Badge>;
    case 'confirmed':
      return <Badge bg="primary" className="px-2 py-1 fw-semibold text-capitalize rounded-pill">Confirmed</Badge>;
    case 'in_progress':
      return <Badge bg="info" className="px-2 py-1 fw-semibold text-capitalize rounded-pill">In Progress</Badge>;
    case 'completed':
      return <Badge bg="success" className="px-2 py-1 fw-semibold text-capitalize rounded-pill">Completed</Badge>;
    case 'cancelled':
      return <Badge bg="danger" className="px-2 py-1 fw-semibold text-capitalize rounded-pill">Cancelled</Badge>;
    default:
      return <Badge bg="secondary" className="px-2 py-1 fw-semibold text-capitalize rounded-pill">{status}</Badge>;
  }
};

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  show,
  onHide,
  selectedOrder,
  updatingOrderStatusId,
  jobCardsList,
  onOpenTicket,
  onOpenCreateJobCard,
  onUpdateStatus,
  onUpdateOrder,
  isInitialEditMode = false,
}) => {
  const [isEditing, setIsEditing] = useState(isInitialEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    deliveryType: 'pickup',
    deliveryAddress: '',
    notes: '',
    status: 'pending',
    paymentStatus: 'pending',
    totalAmount: 0,
    advanceAmount: 0,
  });

  useEffect(() => {
    setIsEditing(isInitialEditMode);
    if (selectedOrder) {
      setFormData({
        customerName: selectedOrder.customerName || '',
        customerPhone: selectedOrder.customerPhone || '',
        customerEmail: selectedOrder.customerEmail || '',
        deliveryType: selectedOrder.deliveryType || 'pickup',
        deliveryAddress: selectedOrder.deliveryAddress || '',
        notes: selectedOrder.notes || '',
        status: selectedOrder.status || 'pending',
        paymentStatus: selectedOrder.paymentStatus || 'pending',
        totalAmount: selectedOrder.totalAmount || selectedOrder.estimatedPrice || 0,
        advanceAmount: selectedOrder.advanceAmount || 0,
      });
    }
  }, [selectedOrder, isInitialEditMode, show]);

  if (!selectedOrder) {
    return null;
  }

  const handleSave = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!onUpdateOrder) return;
    setIsSaving(true);
    try {
      await onUpdateOrder({
        orderId: selectedOrder.orderId,
        customerName: formData.customerName,
        customerPhone: formData.customerPhone,
        customerEmail: formData.customerEmail,
        deliveryType: formData.deliveryType,
        deliveryAddress: formData.deliveryAddress,
        notes: formData.notes,
        status: formData.status,
        paymentStatus: formData.paymentStatus,
        totalAmount: Number(formData.totalAmount) || 0,
        advanceAmount: Number(formData.advanceAmount) || 0,
      });
      setIsEditing(false);
      onHide();
    } catch (err) {
      console.error('Failed to save order updates:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const total = selectedOrder?.totalAmount || selectedOrder?.estimatedPrice || 0;
  const advance = selectedOrder?.advanceAmount || 0;
  const balanceDue = Math.max(0, total - advance);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="admin-order-modal">
      <Modal.Header closeButton className="border-bottom-0 pb-0">
        <div className="d-flex justify-content-between align-items-center w-100 pe-3">
          <Modal.Title className="fw-bold text-dark d-flex align-items-center gap-2">
            {isEditing ? 'Edit Order Details' : 'Order Details'} 
            <span className="text-primary font-monospace" style={{ fontSize: '16px' }}>({selectedOrder.orderId})</span>
          </Modal.Title>
          {onUpdateOrder && (
            <Button
              variant={isEditing ? 'outline-secondary' : 'outline-primary'}
              size="sm"
              className="fw-semibold px-3"
              style={{ borderRadius: '8px', fontSize: '13px' }}
              onClick={() => setIsEditing(!isEditing)}
            >
              <i className={`fas ${isEditing ? 'fa-eye' : 'fa-edit'} me-1`}></i>
              {isEditing ? 'View Details' : 'Edit Order'}
            </Button>
          )}
        </div>
      </Modal.Header>

      <Modal.Body className="pt-2">
        {isEditing ? (
          <Form onSubmit={handleSave} className="p-2">
            <Row className="g-3 mb-4">
              <Col md={12}>
                <h6 className="fw-bold text-dark border-bottom pb-2 mb-3">Customer Information</h6>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-semibold text-dark">Customer Name</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={formData.customerName}
                    onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                    required
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-semibold text-dark">Phone Number</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={formData.customerPhone}
                    onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                    required
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-semibold text-dark">Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    size="sm"
                    value={formData.customerEmail}
                    onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                    required
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mb-4">
              <Col md={12}>
                <h6 className="fw-bold text-dark border-bottom pb-2 mb-3">Status & Pricing</h6>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small fw-semibold text-dark">Order Status</Form.Label>
                  <Form.Select
                    size="sm"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    style={{ borderRadius: '6px' }}
                  >
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small fw-semibold text-dark">Payment Status</Form.Label>
                  <Form.Select
                    size="sm"
                    value={formData.paymentStatus}
                    onChange={(e) => setFormData({ ...formData, paymentStatus: e.target.value })}
                    style={{ borderRadius: '6px' }}
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="failed">Failed</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small fw-semibold text-dark">Total Amount (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    min="0"
                    step="0.01"
                    value={formData.totalAmount}
                    onChange={(e) => setFormData({ ...formData, totalAmount: parseFloat(e.target.value) || 0 })}
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small fw-semibold text-dark">Advance Amount (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    min="0"
                    step="0.01"
                    value={formData.advanceAmount}
                    onChange={(e) => setFormData({ ...formData, advanceAmount: parseFloat(e.target.value) || 0 })}
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col md={12}>
                <h6 className="fw-bold text-dark border-bottom pb-2 mb-3">Fulfillment & Notes</h6>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-semibold text-dark">Delivery Type</Form.Label>
                  <Form.Select
                    size="sm"
                    value={formData.deliveryType}
                    onChange={(e) => setFormData({ ...formData, deliveryType: e.target.value })}
                    style={{ borderRadius: '6px' }}
                  >
                    <option value="pickup">Store Pickup</option>
                    <option value="home">Home Delivery</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              {formData.deliveryType === 'home' && (
                <Col md={8}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold text-dark">Delivery Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      size="sm"
                      value={formData.deliveryAddress}
                      onChange={(e) => setFormData({ ...formData, deliveryAddress: e.target.value })}
                      style={{ borderRadius: '6px' }}
                    />
                  </Form.Group>
                </Col>
              )}
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="small fw-semibold text-dark">Special Instructions / Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    size="sm"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        ) : (
          <Row className="g-4">
            <Col md={12}>
              <div className="bg-light p-4 rounded-4 border border-light mb-1">
                <h5 className="fw-bold text-dark mb-3 border-bottom pb-2">Order Items</h5>
                <div className="table-responsive">
                  <Table hover size="sm" className="bg-white rounded-3 overflow-hidden border mb-0">
                    <thead className="table-light">
                      <tr style={{ fontSize: '11.5px', color: '#64748b' }}>
                        <th className="py-2 px-3">Item / Specifications</th>
                        <th className="py-2 text-center" style={{ width: '80px' }}>Qty</th>
                        <th className="py-2 text-center" style={{ width: '120px' }}>Subtotal</th>
                        <th className="py-2 text-end px-3" style={{ width: '150px' }}>Design Attachment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items && selectedOrder.items.length > 0 ? (
                        selectedOrder.items.map((item: any, idx: number) => (
                          <tr key={idx} style={{ fontSize: '12.5px' }}>
                            <td className="py-2 px-3">
                              <div className="fw-bold text-dark">{item.productName}</div>
                              <div className="text-muted small mt-1" style={{ fontSize: '11px', lineHeight: '1.3' }}>
                                {item.options?.map((opt: any) => (
                                  <div key={opt.key} className="text-capitalize">
                                    {opt.key.replace(/([A-Z])/g, ' $1')}: <span className="fw-semibold text-dark">{opt.value}</span>
                                  </div>
                                ))}
                              </div>
                            </td>
                            <td className="py-2 text-center align-middle">{item.quantity}</td>
                            <td className="py-2 text-center align-middle fw-bold text-dark">₹{(item.estimatedPrice || 0).toLocaleString('en-IN')}</td>
                            <td className="py-2 text-end align-middle px-3">
                              {item.designFileName ? (
                                item.designFileName.startsWith('{') ? (
                                  (() => {
                                    try {
                                      const parsed = JSON.parse(item.designFileName);
                                      return (
                                        <div className="d-flex flex-column align-items-end gap-1">
                                          {parsed.front && (
                                            <a
                                              href={`${UPLOAD_BASE_URL}/${parsed.front}`}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="btn btn-outline-primary btn-sm py-0.5 px-2 fw-semibold"
                                              style={{ borderRadius: '4px', fontSize: '10px', minWidth: '65px' }}
                                              download
                                            >
                                              <i className="fas fa-download"></i> Front
                                            </a>
                                          )}
                                          {parsed.back && (
                                            <a
                                              href={`${UPLOAD_BASE_URL}/${parsed.back}`}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="btn btn-outline-primary btn-sm py-0.5 px-2 fw-semibold"
                                              style={{ borderRadius: '4px', fontSize: '10px', minWidth: '65px' }}
                                              download
                                            >
                                              <i className="fas fa-download"></i> Back
                                            </a>
                                          )}
                                        </div>
                                      );
                                    } catch (_) {
                                      return null;
                                    }
                                  })()
                                ) : (
                                  <a
                                    href={`${UPLOAD_BASE_URL}/${item.designFileName}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn btn-outline-primary btn-sm py-0.5 px-2 fw-semibold"
                                    style={{ borderRadius: '4px', fontSize: '11px' }}
                                    download
                                  >
                                    <i className="fas fa-download"></i> Download
                                  </a>
                                )
                              ) : (
                                <span className="text-muted small">No file</span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr style={{ fontSize: '12.5px' }}>
                          <td className="py-2 px-3">
                            <div className="fw-bold text-dark">{selectedOrder.productName || 'Legacy order'}</div>
                          </td>
                          <td className="py-2 text-center align-middle">{selectedOrder.quantity || 0}</td>
                          <td className="py-2 text-center align-middle fw-bold text-dark">₹{(selectedOrder.estimatedPrice || 0).toLocaleString('en-IN')}</td>
                          <td className="py-2 text-end align-middle px-3">
                            <span className="text-muted small">No file</span>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="bg-light p-4 rounded-4 border border-light h-100">
                <h5 className="fw-bold text-dark mb-3 border-bottom pb-2">Customer Details</h5>
                <Row className="mb-2">
                  <Col xs={4} className="text-muted fw-medium" style={{ fontSize: '12.5px' }}>Name:</Col>
                  <Col xs={8} className="fw-semibold text-dark" style={{ fontSize: '13px' }}>{selectedOrder.customerName}</Col>
                </Row>
                <Row className="mb-2">
                  <Col xs={4} className="text-muted fw-medium" style={{ fontSize: '12.5px' }}>Phone:</Col>
                  <Col xs={8} className="fw-semibold text-dark" style={{ fontSize: '13px' }}>
                    <a href={`tel:${selectedOrder.customerPhone}`} className="text-decoration-none">{selectedOrder.customerPhone}</a>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col xs={4} className="text-muted fw-medium" style={{ fontSize: '12.5px' }}>Email:</Col>
                  <Col xs={8} className="text-dark" style={{ fontSize: '13px', wordBreak: 'break-all' }}>
                    <a href={`mailto:${selectedOrder.customerEmail}`} className="text-decoration-none">{selectedOrder.customerEmail}</a>
                  </Col>
                </Row>

                <h5 className="fw-bold text-dark mt-4 mb-3 border-bottom pb-2">Shipping Information</h5>
                <Row className="mb-2">
                  <Col xs={4} className="text-muted fw-medium" style={{ fontSize: '12.5px' }}>Delivery:</Col>
                  <Col xs={8} className="fw-semibold text-dark text-capitalize" style={{ fontSize: '13px' }}>
                    {selectedOrder.deliveryType === 'pickup' ? 'Store Pickup' : 'Home Delivery'}
                  </Col>
                </Row>
                {selectedOrder.deliveryType === 'home' && (
                  <Row className="mb-2">
                    <Col xs={4} className="text-muted fw-medium" style={{ fontSize: '12.5px' }}>Address:</Col>
                    <Col xs={8} className="text-dark" style={{ fontSize: '12.5px', lineHeight: '1.4' }}>{selectedOrder.deliveryAddress}</Col>
                  </Row>
                )}

                {selectedOrder.notes && (
                  <div className="mt-3 bg-white p-2 rounded-3 border border-light" style={{ fontSize: '12px' }}>
                    <strong>Instructions:</strong>
                    <p className="mb-0 text-muted mt-1" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>{selectedOrder.notes}</p>
                  </div>
                )}
              </div>
            </Col>

            <Col md={6}>
              <div className="bg-light p-4 rounded-4 border border-light h-100">
                <h5 className="fw-bold text-dark mb-3 border-bottom pb-2">Payment & Status Details</h5>
                <Row className="mb-2">
                  <Col xs={5} className="text-muted fw-medium" style={{ fontSize: '12.5px' }}>Order Status:</Col>
                  <Col xs={7}>{getStatusBadge(selectedOrder.status)}</Col>
                </Row>
                <Row className="mb-2">
                  <Col xs={5} className="text-muted fw-medium" style={{ fontSize: '12.5px' }}>Payment Status:</Col>
                  <Col xs={7} className="fw-bold" style={{ fontSize: '13.0px' }}>
                    {selectedOrder.paymentStatus === 'paid' ? (
                      <span className="text-success"><i className="fas fa-check-circle me-1"></i>Paid</span>
                    ) : (
                      <span className="text-warning"><i className="fas fa-exclamation-circle me-1"></i>Pending Payment</span>
                    )}
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col xs={5} className="text-muted fw-medium" style={{ fontSize: '12.5px' }}>Total Price:</Col>
                  <Col xs={7} className="fw-bold text-primary" style={{ fontSize: '15px' }}>
                    ₹{total.toLocaleString('en-IN')}
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col xs={5} className="text-muted fw-medium" style={{ fontSize: '12.5px' }}>Advance Paid:</Col>
                  <Col xs={7} className="fw-bold text-success" style={{ fontSize: '14px' }}>
                    ₹{advance.toLocaleString('en-IN')}
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col xs={5} className="text-muted fw-medium" style={{ fontSize: '12.5px' }}>Balance Due:</Col>
                  <Col xs={7} className={`fw-bold ${balanceDue > 0 ? 'text-danger' : 'text-success'}`} style={{ fontSize: '14px' }}>
                    ₹{balanceDue.toLocaleString('en-IN')}
                  </Col>
                </Row>

                {selectedOrder.razorpayOrderId && (
                  <div className="mt-4 border-top pt-3" style={{ fontSize: '12px' }}>
                    <div className="mb-1 text-muted"><strong>Razorpay Order ID:</strong></div>
                    <div className="font-monospace bg-white border p-1 rounded mb-2 text-dark">{selectedOrder.razorpayOrderId}</div>

                    {selectedOrder.razorpayPaymentId && (
                      <>
                        <div className="mb-1 text-muted"><strong>Razorpay Payment ID:</strong></div>
                        <div className="font-monospace bg-white border p-1 rounded text-dark">{selectedOrder.razorpayPaymentId}</div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </Col>

            <Col md={12}>
              <div className="mt-2 bg-light p-3 rounded-4 border border-light d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span className="fw-bold text-dark" style={{ fontSize: '13.5px' }}>Quick Status Update:</span>
                <div className="d-flex align-items-center gap-2">
                  {updatingOrderStatusId === selectedOrder.orderId && (
                    <Spinner size="sm" animation="border" className="text-primary" />
                  )}
                  <Form.Select
                    value={selectedOrder.status}
                    onChange={(e) => onUpdateStatus(selectedOrder.orderId, e.target.value)}
                    style={{ width: '180px', borderRadius: '8px', fontSize: '13.5px' }}
                    disabled={updatingOrderStatusId === selectedOrder.orderId}
                  >
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </Form.Select>
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Modal.Body>

      <Modal.Footer className="border-top-0 pt-0">
        {isEditing ? (
          <div className="d-flex gap-2 w-100 justify-content-end">
            <Button variant="outline-secondary" onClick={onHide} disabled={isSaving} style={{ borderRadius: '8px', fontSize: '13px' }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => handleSave()} disabled={isSaving} style={{ borderRadius: '8px', fontSize: '13px' }}>
              {isSaving ? <><Spinner size="sm" animation="border" me-1 /> Saving...</> : 'Save Order Changes'}
            </Button>
          </div>
        ) : (
          <Button variant="secondary" onClick={onHide} style={{ borderRadius: '8px', fontSize: '13px' }}>
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default OrderDetailsModal;
